import React, { useState, useMemo } from "react";
import Loader from "react-loader-spinner";
import useGetCatalog from "../../../hooks/Inventory/useGetCatalog";
import useGetImage from "../../../hooks/useGetImage";
import { config } from "../../../utils/conf";

function ItemImage({ mediaUrl, itemId }) {
  const imagePath = mediaUrl || `${config.rpcImagePath}${itemId}.png`;
  const fallback = `${config.rpcImagePath}default.png`;
  const imageQuery = useGetImage(imagePath, fallback);

  return (
    <div className="tw-w-16 tw-h-16 tw-flex tw-items-center tw-justify-center">
      {imageQuery.isFetching ? (
        <Loader
          type="ThreeDots"
          color="rgba(246,130,32,1)"
          height="20"
          width="20"
        />
      ) : (
        <img
          className="tw-object-contain tw-w-full tw-h-full"
          src={imageQuery.data}
          alt={itemId}
        />
      )}
    </div>
  );
}

export default function InventoryReview() {
  const { data: inventory, isLoading, isError } = useGetCatalog();
  const [sortField, setSortField] = useState("itemId");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filter, setFilter] = useState("");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredAndSorted = useMemo(() => {
    if (!inventory || !Array.isArray(inventory)) return [];
    let items = [...inventory];

    if (filter) {
      const lowerFilter = filter.toLowerCase();
      items = items.filter(
        (item) =>
          (item.itemId && item.itemId.toLowerCase().includes(lowerFilter)) ||
          (item.itemClassId &&
            item.itemClassId.toLowerCase().includes(lowerFilter)) ||
          (item.itemBrand &&
            item.itemBrand.toLowerCase().includes(lowerFilter)) ||
          (item.productDescription &&
            item.productDescription.toLowerCase().includes(lowerFilter)),
      );
    }

    items.sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? cmp : -cmp;
    });

    return items;
  }, [inventory, filter, sortField, sortDirection]);

  const columns = [
    { field: "image", label: "", sortable: false },
    { field: "itemId", label: "Item Id" },
    { field: "itemClassId", label: "Item Class" },
    { field: "itemBrand", label: "Brand" },
    { field: "productDescription", label: "Description" },
    { field: "totalQuantity", label: "Truckload Quantity" },
  ];

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  return (
    <div className="tw-bg-white tw-px-4 tw-py-4">
      <h2 className="tw-text-xl tw-font-semibold tw-mb-4">Inventory Review</h2>

      <div className="tw-mb-4">
        <input
          type="text"
          placeholder="Filter by item, class, brand, or description..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-w-full tw-max-w-md tw-text-sm"
        />
      </div>

      <div className="tw-align-middle tw-inline-block tw-min-w-full tw-shadow tw-overflow-hidden tw-rounded-lg tw-border-b tw-border-gray-200">
        <table className="tw-min-w-full">
          <thead>
            <tr className="tw-bg-tosca-gray">
              {columns.map((col) => (
                <th
                  key={col.field}
                  onClick={
                    col.sortable !== false
                      ? () => handleSort(col.field)
                      : undefined
                  }
                  className={`tw-px-3 tw-py-3 tw-border-b tw-border-gray-200 tw-bg-gray-50 tw-text-left tw-text-xs tw-leading-4 tw-font-medium tw-text-gray-900 tw-uppercase tw-tracking-wider ${
                    col.sortable !== false
                      ? "tw-cursor-pointer hover:tw-bg-gray-300 tw-underline"
                      : ""
                  }`}
                >
                  {col.label}
                  {col.sortable !== false && renderSortIndicator(col.field)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-bg-white">
            {isLoading && (
              <tr>
                <td colSpan={columns.length} className="tw-text-center tw-py-8">
                  <Loader
                    type="Oval"
                    color="rgba(246,130,32,1)"
                    height="50"
                    width="50"
                  />
                </td>
              </tr>
            )}

            {isError && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="tw-text-center tw-py-8 tw-text-red-500"
                >
                  An error occurred loading inventory data.
                </td>
              </tr>
            )}

            {!isLoading && !isError && filteredAndSorted.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="tw-text-center tw-py-8 tw-text-gray-500"
                >
                  No inventory items found.
                </td>
              </tr>
            )}

            {!isLoading &&
              filteredAndSorted.map((item, index) => (
                <tr
                  key={item.itemId || index}
                  className="hover:tw-bg-tosca-alice-blue hover:tw-text-gray-900"
                >
                  <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                    <ItemImage mediaUrl={item.mediaUrl} itemId={item.itemId} />
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-sm">
                    {item.itemId}
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-sm">
                    {item.itemClassId}
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-sm">
                    {item.itemBrand}
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-sm">
                    {item.productDescription}
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-sm tw-text-right">
                    {item.totalQuantity}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!isLoading && filteredAndSorted.length > 0 && (
        <div className="tw-mt-2 tw-text-sm tw-text-gray-500">
          {filteredAndSorted.length} item
          {filteredAndSorted.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}
